import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto';
import MenuIcon from '@mui/icons-material/Menu';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { RootState } from '../../redux/store';
import { activeNavRoute, navItems, useNavCounts } from '../config/navigation';
import type { ColorPaletteName } from '../config/theme';
import {
    setColorPalette,
    setTheme,
    ThemeMode,
} from '../redux/preferencesSlice';

const THEME_OPTIONS: Array<{
    mode: ThemeMode;
    label: string;
    icon: React.ReactNode;
}> = [
    { mode: 'light', label: 'Light', icon: <Brightness7Icon sx={{ fontSize: '1.2rem' }} /> },
    { mode: 'dark', label: 'Dark', icon: <Brightness4Icon sx={{ fontSize: '1.2rem' }} /> },
    { mode: 'system', label: 'System', icon: <BrightnessAutoIcon sx={{ fontSize: '1.2rem' }} /> },
    { mode: 'daytime', label: 'Auto (7am-7pm)', icon: <WbTwilightIcon sx={{ fontSize: '1.2rem' }} /> },
];

// The swatches the VictorIA UI shows for each palette.
const PALETTE_OPTIONS: Array<{
    name: ColorPaletteName;
    label: string;
    colors: [string, string];
}> = [
    { name: 'default', label: 'Default', colors: ['#c9b8ff', '#5b3fa6'] },
    { name: 'ocean', label: 'Ocean', colors: ['#00b4db', '#0277bd'] },
    { name: 'forest', label: 'Forest', colors: ['#66bb6a', '#2e7d32'] },
    { name: 'sunset', label: 'Sunset', colors: ['#ff7043', '#d84315'] },
    { name: 'midnight', label: 'Midnight', colors: ['#8e44ad', '#512da8'] },
    { name: 'sage', label: 'Sage', colors: ['#80cbc4', '#00897b'] },
    { name: 'coral', label: 'Coral', colors: ['#ff8a80', '#e53935'] },
    { name: 'gold', label: 'Gold', colors: ['#ffd54f', '#f57f17'] },
    { name: 'arctic', label: 'Arctic', colors: ['#9ad1ff', '#1b6fb0'] },
    { name: 'nebula', label: 'Nebula', colors: ['#e879f9', '#a21caf'] },
    { name: 'copper', label: 'Copper', colors: ['#d9926a', '#a4501f'] },
    { name: 'indigo', label: 'Indigo', colors: ['#818cf8', '#4338ca'] },
    { name: 'mocha', label: 'Mocha', colors: ['#c8a27a', '#8a5a2b'] },
    { name: 'matcha', label: 'Matcha', colors: ['#a3e635', '#4d7c0f'] },
    { name: 'mono', label: 'Mono', colors: ['#e4e4e7', '#27272a'] },
    { name: 'blush', label: 'Blush', colors: ['#f4b6c2', '#b83f63'] },
];

const Swatch: React.FC<{ colors: [string, string] }> = ({ colors }) => (
    <Box
        sx={{
            display: 'flex',
            width: '24px',
            height: '24px',
            borderRadius: '4px',
            overflow: 'hidden',
        }}
    >
        <Box sx={{ flex: 1, backgroundColor: colors[0] }} />
        <Box sx={{ flex: 1, backgroundColor: colors[1] }} />
    </Box>
);

/**
 * The left-hand menu, opened from the header's menu button. Below the desktop
 * breakpoint the header's tabs are gone, so the pages are listed here too.
 */
export function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const counts = useNavCounts();
    const [open, setOpen] = useState(false);
    const [themeAnchorEl, setThemeAnchorEl] = useState<null | HTMLElement>(null);
    const [paletteAnchorEl, setPaletteAnchorEl] = useState<null | HTMLElement>(null);

    const themeMode = useSelector((state: RootState) => state.preferences.theme);
    const colorPalette = useSelector(
        (state: RootState) => state.preferences.colorPalette
    );
    const currentPalette = PALETTE_OPTIONS.find((p) => p.name === colorPalette);
    const activeRoute = activeNavRoute(location.pathname);

    const toggleDrawer = (newOpen: boolean) => () => setOpen(newOpen);

    const DrawerList = (
        <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={toggleDrawer(false)}
        >
            {!isDesktop && (
                <>
                    <List>
                        {navItems.map((item) => {
                            const count = counts[item.route];
                            return (
                                <ListItem key={item.route} disablePadding>
                                    <ListItemButton
                                        selected={activeRoute === item.route}
                                        onClick={() => navigate(item.route)}
                                    >
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText
                                            primary={
                                                count === undefined
                                                    ? item.text
                                                    : `${item.text} (${count})`
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                    <Divider />
                </>
            )}

            <List>
                <ListItem disablePadding onClick={(event) => event.stopPropagation()}>
                    <ListItemButton
                        onClick={(event) => setThemeAnchorEl(event.currentTarget)}
                    >
                        <ListItemIcon>
                            {THEME_OPTIONS.find((t) => t.mode === themeMode)?.icon}
                        </ListItemIcon>
                        <ListItemText primary="Theme" />
                    </ListItemButton>
                </ListItem>
                <Menu
                    anchorEl={themeAnchorEl}
                    open={Boolean(themeAnchorEl)}
                    onClose={() => setThemeAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    {THEME_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.mode}
                            onClick={() => {
                                dispatch(setTheme(option.mode));
                                setThemeAnchorEl(null);
                            }}
                            selected={themeMode === option.mode}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '24px' }}>
                                {option.icon}
                            </Box>
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>

                <ListItem disablePadding onClick={(event) => event.stopPropagation()}>
                    <ListItemButton
                        onClick={(event) => setPaletteAnchorEl(event.currentTarget)}
                    >
                        <ListItemIcon>
                            <Swatch colors={currentPalette?.colors ?? ['#c9b8ff', '#5b3fa6']} />
                        </ListItemIcon>
                        <ListItemText primary="Color Palette" />
                    </ListItemButton>
                </ListItem>
                <Menu
                    anchorEl={paletteAnchorEl}
                    open={Boolean(paletteAnchorEl)}
                    onClose={() => setPaletteAnchorEl(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    {PALETTE_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.name}
                            onClick={() => {
                                dispatch(setColorPalette(option.name));
                                setPaletteAnchorEl(null);
                            }}
                            selected={colorPalette === option.name}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                        >
                            <Swatch colors={option.colors} />
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            </List>
        </Box>
    );

    return (
        <div>
            <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 1 }}
                onClick={toggleDrawer(true)}
            >
                <MenuIcon />
            </IconButton>
            <Drawer anchor="left" open={open} onClose={toggleDrawer(false)}>
                {DrawerList}
            </Drawer>
        </div>
    );
}
